import { fabric } from 'fabric';

import { InternalCanvas, InternalCanvasInit } from './canvas';
import { StateAction, StateActionHistory } from './history';
import { DrawingTool } from './tools';
import { ObjectBuilder } from './tools/tool';
import { SerializedCanvas, SerializedObject } from './types';
import {
    flattenObjects,
    isActiveSelection,
    isMap,
    isTextbox,
    normalizeObjectUUID,
    serializeCanvas,
    serializeObject,
} from './util';

declare module 'fabric' {
    // eslint-disable-next-line @typescript-eslint/no-namespace
    export namespace fabric {
        export interface Object {
            uuid: string;
            imagetype?: 'map-overhead' | 'character-icon';
            imagekey?: string;
        }
        export interface Canvas {
            toJSON(customPropsToSerialize: string[]): SerializedCanvas;
        }
        export interface IText {
            doneEditing(e: fabric.IEvent): void;
        }
        export interface Polyline {
            _setPositionDimensions(options: Record<string, any>): void;
        }
    }
}

const STICKER_IMAGE_SIZE = 32;

export interface DrawingCanvasEventInit extends EventInit {
    canvas: DrawingCanvas;
    objects?: fabric.Object[];
    object?: fabric.Object;
    moving?: boolean;
    x?: number;
    y?: number;
}

export class DrawingCanvasEvent extends Event {
    constructor(type: string, private init: DrawingCanvasEventInit) {
        super(type, init);
    }

    public get canvas(): DrawingCanvas {
        return this.init.canvas;
    }

    public get objects(): fabric.Object[] | undefined {
        return this.init.objects;
    }

    public get object(): fabric.Object | undefined {
        return this.init.object;
    }

    public get moving(): boolean | undefined {
        return this.init.moving;
    }

    public get x(): number | undefined {
        return this.init.x;
    }

    public get y(): number | undefined {
        return this.init.y;
    }
}

class ObjectStateCache {
    private _cache: { [uuid: string]: SerializedObject } = {};

    public put(objs: fabric.Object | fabric.Object[]): void {
        for (const obj of Array.isArray(objs) ? objs : [objs]) {
            if (isActiveSelection(obj)) {
                throw new Error(
                    'active selection may not be put into object state cache',
                );
            }

            this._cache[normalizeObjectUUID(obj)] = serializeObject(obj);
        }
    }

    public get(obj: string | fabric.Object): SerializedObject | undefined {
        return this._cache[normalizeObjectUUID(obj)];
    }

    public remove(obj: string | fabric.Object): void {
        delete this._cache[normalizeObjectUUID(obj)];
    }

    public has(obj: string | fabric.Object): boolean {
        return normalizeObjectUUID(obj) in this._cache;
    }

    public clear(): void {
        this._cache = {};
    }
}

function wrapObjectEventHandler(
    handler: (obj: fabric.Object) => void,
): ({ target }: fabric.IEvent) => void {
    return ({ target }: fabric.IEvent) => target && handler(target);
}

const calcGridRowCol = (COLS: number) => (index: number): [number, number] => {
    const row = Math.floor(index / COLS);
    const col = index % COLS;
    return [row, col];
};

export interface DrawingCanvasOptions
    extends Omit<InternalCanvasInit, 'toolMode'> {
    initialTool?: DrawingTool;
    initialHistory?: StateActionHistory<SerializedObject>;
}

export class DrawingCanvas extends EventTarget {
    private _canvas: InternalCanvas;
    private _history: StateActionHistory<SerializedObject>;

    private _cleanupFuncs: Array<() => void>;
    private _cachedObjectStates = new ObjectStateCache();

    private _currentTool: DrawingTool;
    private _objectBuilder: ObjectBuilder | undefined;

    public constructor(
        canvasElem: HTMLCanvasElement,
        {
            initialTool = DrawingTool.NONE,
            initialHistory = new StateActionHistory<SerializedObject>({}),
            ...internalCanvasOptions
        }: DrawingCanvasOptions = {},
    ) {
        super();

        this._canvas = new InternalCanvas(canvasElem, {
            ...internalCanvasOptions,
            toolMode: initialTool.mode,
        });
        this._history = initialHistory;
        this._currentTool = initialTool;
        this._cleanupFuncs = [
            this._canvas.addFabricEventHandler(
                'object:added',
                wrapObjectEventHandler((obj: fabric.Object) => {
                    this._canvas.adoptObject(obj);

                    const { width, height } = obj;
                    const isPath = obj.isType('path');
                    const isLine = obj.isType('line');
                    if (isPath) {
                        obj.set({ perPixelTargetFind: true });
                    }

                    // If the object is worth keeping track of, keep track of it
                    if (
                        !isPath &&
                        !(isLine && (width || height)) &&
                        !(width && height)
                    ) {
                        return;
                    }

                    this._cachedObjectStates.put(obj);
                    this._history.pushStateEntryFromAction({
                        type: 'added',
                        after: serializeObject(obj),
                    });

                    this._history.commit();

                    this._dispatchCanvasObjectEvent('objectsadded', obj);
                }),
            ),
            this._canvas.addFabricEventHandler(
                'object:modified',
                wrapObjectEventHandler(async (obj: fabric.Object) => {
                    if (isTextbox(obj) && !obj.text) {
                        const before = this._cachedObjectStates.get(obj);
                        if (!before) {
                            return;
                        }

                        // remove textboxes with no text
                        this._history.enqueueAction({
                            type: 'removed',
                            before,
                        });

                        this._history.commit();

                        this._dispatchCanvasObjectEvent('objectsremoved', obj);
                    } else {
                        const objs = await flattenObjects(
                            obj,
                            isActiveSelection(obj),
                        );
                        for (const obj of objs) {
                            const before = this._cachedObjectStates.get(obj);
                            const after = serializeObject(obj);
                            this._cachedObjectStates.put(obj);

                            if (before) {
                                this._history.enqueueAction({
                                    type: 'modified',
                                    before,
                                    after,
                                });
                            } else {
                                this._history.enqueueAction({
                                    type: 'added',
                                    after,
                                });
                            }
                        }

                        this._history.commit();

                        this._dispatchCanvasObjectEvent(
                            'objectsmodified',
                            objs,
                        );
                    }
                }),
            ),
            this._canvas.addFabricEventHandler(
                'object:removed',
                wrapObjectEventHandler(async (objs: fabric.Object) => {
                    for (const obj of await flattenObjects(objs)) {
                        this._history.enqueueAction({
                            type: 'removed',
                            before: serializeObject(obj),
                        });
                    }

                    this._history.commit();

                    this._dispatchCanvasObjectEvent('objectsremoved', objs);
                }),
            ),
            this._canvas.addFabricEventHandler(
                ['object:moving', 'object:scaling', 'object:rotating'],
                wrapObjectEventHandler((obj: fabric.Object) => {
                    this._dispatchCanvasObjectEvent(
                        'objectmodified',
                        obj,
                        true,
                    );
                }),
            ),
            this._canvas.addFabricEventHandler(
                'text:editing:exited',
                wrapObjectEventHandler((obj: fabric.Object) => {
                    if ((obj as fabric.IText).text) {
                        this._canvas.discardActiveObject();
                    } else if (this._cachedObjectStates.has(obj.uuid)) {
                        // delete object and add to state history
                        this._cachedObjectStates.remove(obj);
                    } else {
                        // object was never in the state, so there shouldn't be an undo action
                        this._canvas.removeObjectSilently(obj);
                        this._canvas.discardActiveObject();
                    }
                }),
            ),
            this._canvas.addFabricEventHandler(
                'mouse:down',
                ({ target, absolutePointer, button, e }: fabric.IEvent) => {
                    if (!absolutePointer) {
                        return;
                    }

                    const { x, y } = absolutePointer;
                    if (
                        this._currentTool === DrawingTool.NONE &&
                        button === 1
                    ) {
                        if ((e as PointerEvent).altKey) {
                            if (
                                this._dispatchCanvasEvent('ping', {
                                    x,
                                    y,
                                    cancelable: true,
                                })
                            ) {
                                this.renderPing(x, y);
                            }
                        } else {
                            this._dispatchCanvasEvent('click', { x, y });
                        }

                        return;
                    }

                    if (this._canvas._fabricCanvas.selection) {
                        return;
                    }

                    if (
                        target &&
                        this._currentTool === DrawingTool.TEXTBOX &&
                        isTextbox(target)
                    ) {
                        this._canvas._fabricCanvas.setActiveObject(target);
                        return;
                    }

                    if (
                        !this._dispatchCanvasEvent('drawstart', {
                            x,
                            y,
                            cancelable: true,
                        })
                    ) {
                        return;
                    }

                    this._objectBuilder = this._currentTool.createObjectBuilder(
                        this._canvas,
                        absolutePointer,
                    );
                    this._updateBuilderObject();
                    this._canvas.requestRenderAll();
                },
            ),
            this._canvas.addFabricEventHandler(
                'mouse:move',
                (event: fabric.IEvent) => {
                    if (
                        this._currentTool.handleMouseMove(this._canvas, event)
                    ) {
                        this._canvas.requestRenderAll();
                    } else if (this._objectBuilder) {
                        this._objectBuilder.update(event);
                        this._canvas.replaceObjectsSilently(
                            this._objectBuilder.object,
                            true,
                        );
                        this._canvas.requestRenderAll();
                    }
                },
            ),
            this._canvas.addFabricEventHandler(
                'mouse:up',
                (event: fabric.IEvent) => {
                    if (this._objectBuilder) {
                        this._objectBuilder.update(event);
                    }

                    if (
                        this._finalizeBuilderObject(
                            !(event.e as PointerEvent).altKey,
                        )
                    ) {
                        this._canvas.requestRenderAll();
                    } else if (
                        (event.e as PointerEvent).altKey &&
                        event.absolutePointer
                    ) {
                        const { x, y } = event.absolutePointer;
                        if (
                            this._dispatchCanvasEvent('ping', {
                                x,
                                y,
                                cancelable: true,
                            })
                        ) {
                            this.renderPing(x, y);
                        }
                    }
                },
            ),
            this._canvas.addFabricEventHandler(
                'mouse:wheel',
                ({ e }: fabric.IEvent) => {
                    if (!this.objects.length) {
                        return;
                    }

                    const sel = this._canvas.activeObject;
                    if (!sel) {
                        return;
                    }

                    const deltaY = (e as WheelEvent).deltaY;
                    const width =
                        sel.getScaledWidth() *
                        Math.pow(1.1, -deltaY / Math.abs(deltaY));
                    if (width <= 10 || 10000 <= width) {
                        return;
                    }

                    const center = sel.getCenterPoint();
                    sel.set({
                        left: center.x,
                        top: center.y,
                        originX: 'center',
                        originY: 'center',
                    });

                    sel.scaleToWidth(width, true);

                    this._dispatchCanvasObjectEvent('objectsmodified', sel);

                    this._canvas.requestRenderAll();
                    e.preventDefault();
                    e.stopPropagation();
                },
            ),
        ];
    }

    public get canUndo(): boolean {
        return this._history.canUndo();
    }

    public get canRedo(): boolean {
        return this._history.canRedo();
    }

    public get tool(): DrawingTool {
        return this._currentTool;
    }

    public set tool(tool: DrawingTool) {
        this.setTool(tool);
    }

    public setTool(tool: DrawingTool | undefined | null): void {
        if (!tool) {
            tool = DrawingTool.NONE;
        }

        if (this._currentTool === tool) {
            return;
        }

        this._canvas.toolMode = tool.mode;

        this._canvas.discardActiveObject();
        this._canvas.requestRenderAll();
        this._currentTool = tool;
    }

    public get objects(): fabric.Object[] {
        return this._canvas._fabricCanvas._objects;
    }

    public get toolSize(): number {
        return this._canvas.brushWidth;
    }

    public set toolSize(width: number) {
        this.setToolSize(width);
    }

    public setToolSize(width: number): void {
        this._canvas.brushWidth = width;
    }

    public get toolColor(): string {
        return this._canvas.brushColor;
    }

    public set toolColor(color: string) {
        this.setToolColor(color);
    }

    public setToolColor(color: string): void {
        this._canvas.brushColor = color;
    }

    private async _applyStateAction(
        action: StateAction<SerializedObject>,
        undo: boolean,
    ): Promise<fabric.Object> {
        let ded: SerializedObject;
        let op: (obj: fabric.Object) => void;
        switch (action.type) {
            case 'added':
                ded = action.after;
                op = undo
                    ? this._canvas.removeObjectSilently
                    : this._canvas.replaceObjectsSilently;
                break;

            case 'modified':
                ded = undo ? action.before : action.after;
                op = this._canvas.replaceObjectsSilently;
                break;

            case 'removed':
                ded = action.before;
                op = undo
                    ? this._canvas.replaceObjectsSilently
                    : this._canvas.removeObjectSilently;
                break;
        }

        const obj = await this._canvas.enlivenObject(ded);
        op.call(this._canvas, obj);
        return obj;
    }

    private async _applyStateActions(
        actions: Array<StateAction<SerializedObject>>,
        undo = false,
    ): Promise<[fabric.Object[], fabric.Object[]]> {
        const added: fabric.Object[] = [];
        const removed: fabric.Object[] = [];
        for (const action of actions) {
            const obj = await this._applyStateAction(action, undo);
            switch (action.type) {
                case 'added':
                    added.push(obj);
                    break;

                case 'removed':
                    removed.push(obj);
                    break;
            }

            if (
                action.type === 'modified' ||
                (undo && action.type === 'removed') ||
                (!undo && action.type === 'added')
            ) {
                this._cachedObjectStates.put(obj);
                for (const o of await flattenObjects(obj)) {
                    o.set({ dirty: true });
                    if (isMap(o)) {
                        o.sendToBack();
                    }
                }
            }
        }

        this._canvas.discardActiveObject();
        this._canvas.requestRenderAll();

        return [added, removed];
    }

    public async setColor(color: string): Promise<void> {
        if (color === this.toolColor) {
            return;
        }

        this.toolColor = color;

        const active = this._canvas.activeObject;
        if (!active) {
            return;
        }

        const objs = await flattenObjects(active);
        for (const obj of objs) {
            if (obj.stroke) {
                obj.set({ stroke: color, dirty: true });
            }

            if (obj.fill) {
                obj.set({ fill: color, dirty: true });
            }
        }

        this._dispatchCanvasObjectEvent('objectsmodified', objs);

        this._canvas.requestRenderAll();
    }

    public async undo(): Promise<void> {
        if (!this._dispatchCanvasEvent('undo')) {
            return;
        }

        const actions = this._history.undoAndGetPreviousStateEntry()?.actions;
        if (!actions) {
            return;
        }

        const [removed, added] = await this._applyStateActions(actions, true);

        this._dispatchCanvasObjectEvent('objectsadded', added);
        this._dispatchCanvasObjectEvent('objectsremoved', removed);
    }

    public async redo(): Promise<void> {
        if (!this._dispatchCanvasEvent('redo')) {
            return;
        }

        const actions = this._history.redoAndGetNextStateEntry()?.actions;
        if (!actions) {
            return;
        }

        const [added, removed] = await this._applyStateActions(actions, false);

        this._dispatchCanvasObjectEvent('objectsadded', added);
        this._dispatchCanvasObjectEvent('objectsremoved', removed);
    }

    public renderPing(x: number, y: number, color = '#FF6600'): void {
        const p = new fabric.Circle({
            left: x,
            top: y,
            selectable: false,
            originX: 'center',
            originY: 'center',
            fill: 'transparent',
            stroke: color,
        });
        p.excludeFromExport = true;
        p.canvas = this._canvas._fabricCanvas;
        p.setCoords();
        this._canvas.addObjectsSilently(p);
        p.animate('radius', 15, {
            duration: 500,
            onChange: () => this._canvas.requestRenderAll(),
            easing: fabric.util.ease.easeOutElastic,
            onComplete: () => {
                this._canvas.removeObjectsSilently(p);
                this._canvas.requestRenderAll();
            },
        });
    }

    public async trash(): Promise<void> {
        if (!this._dispatchCanvasEvent('trash', { cancelable: true })) {
            return;
        }

        let objects: fabric.Object[];
        if (this._canvas.activeObject) {
            objects = await flattenObjects(this._canvas.activeObject);
        } else {
            objects = this.objects.filter((obj) => !isMap(obj));
            if (!objects.length) {
                objects = this.objects;
            }
        }

        this._canvas.discardActiveObject();
        this.removeObjects(objects);
    }

    public async addStickerFromURL(
        url: string,
        stickerNum: number,
        imagekey: string,
    ): Promise<void> {
        const obj = await new Promise<fabric.Image>((resolve) =>
            fabric.Image.fromURL(url, resolve),
        );

        const centerRows = 2;
        const centerColumns = 6;
        const centerLimit = centerRows * centerColumns;

        const zoom = this._canvas.zoom;
        const canvasWidth = this._canvas.width / zoom;
        const canvasHeight = this._canvas.height / zoom;
        let row, col, top, left;
        if (stickerNum < centerLimit) {
            // place in 2 rows in center
            [row, col] = calcGridRowCol(centerColumns)(stickerNum);
            top =
                row * STICKER_IMAGE_SIZE +
                canvasHeight / 2 -
                (centerRows / 2) * STICKER_IMAGE_SIZE;
            left =
                col * STICKER_IMAGE_SIZE +
                canvasWidth / 2 -
                (centerColumns / 2) * STICKER_IMAGE_SIZE;
        } else {
            // position from top left
            const columns = canvasWidth / STICKER_IMAGE_SIZE;
            [row, col] = calcGridRowCol(columns)(stickerNum - centerLimit);
            top = row * STICKER_IMAGE_SIZE;
            left = col * STICKER_IMAGE_SIZE;
        }

        obj.set({
            top,
            left,
            originX: 'left',
            originY: 'top',
            imagetype: 'character-icon',
            imagekey,
        });

        obj.scaleToWidth(STICKER_IMAGE_SIZE);
        obj.scaleToHeight(STICKER_IMAGE_SIZE);

        this._canvas.addObjects(obj);
    }

    public async setUnderlayFromURL(
        url: string,
        imagekey: string,
    ): Promise<void> {
        const obj = await new Promise<fabric.Image>((resolve) =>
            fabric.Image.fromURL(url, resolve),
        );

        if (!obj.width || !obj.height || !obj.scaleX || !obj.scaleY) {
            throw new Error('underlay object is missing dimensions');
        }

        // Only 1 map allowed. Remove current one.
        for (const obj of this.objects) {
            if (!isMap(obj)) {
                continue;
            }

            this._canvas.removeObjects(obj);
        }

        const zoom = this._canvas.zoom;
        const canvasWidth = this._canvas.width / zoom;
        const canvasHeight = this._canvas.height / zoom;
        const imgWidth = obj.width * obj.scaleX;
        const imgHeight = obj.height * obj.scaleY;

        const left = 0;
        const top = 0;
        let scaleFactor = 0;

        // Maximise for width or height whichever is smaller
        if (canvasWidth - imgWidth >= canvasHeight - imgHeight) {
            scaleFactor = canvasHeight / imgHeight;
        } else {
            scaleFactor = canvasWidth / imgWidth;
        }

        obj.set({
            top,
            left,
            originX: 'left',
            originY: 'top',
            scaleX: scaleFactor,
            scaleY: scaleFactor,
            imagetype: 'map-overhead',
            imagekey,
        });

        this._canvas.addObjects(obj);
        this._canvas.requestRenderAll();
    }

    public async import(canvas: SerializedCanvas): Promise<void> {
        this.reset();

        // B A C K W A R D S C O M P A T I B I L I T Y
        if (typeof canvas === 'string') {
            canvas = JSON.parse(canvas);
        }

        const objects = await this._canvas.enlivenObjects(canvas.objects);
        await this._canvas.addObjectsSilently(objects);
        this._cachedObjectStates.put(objects);

        this._canvas.requestRenderAll();
        this._dispatchCanvasEvent('canvasimported');
    }

    public export(): SerializedCanvas {
        return serializeCanvas(this._canvas);
    }

    public clearSilently(): void {
        const removedObjects = this.objects;
        this._canvas._fabricCanvas._objects = [];
        this._cachedObjectStates.clear();
        this._canvas.requestRenderAll();

        this._dispatchCanvasObjectEvent(
            'objectsremoved:silent',
            removedObjects,
        );
        this._dispatchCanvasEvent('canvascleared:silent');
    }

    public async replaceObjectsSilently(
        ded: SerializedObject[],
    ): Promise<void> {
        const objs = await this._canvas.enlivenObjects(ded);
        this._cachedObjectStates.put(objs);
        this._canvas.replaceObjectsSilently(objs, true);
        this._canvas.requestRenderAll();

        this._dispatchCanvasObjectEvent('objectsreplaced:silent', objs);
    }

    public removeObjectsSilently(uuids: string[]): void {
        const removedObjects = this._canvas
            .getObjectsByUUIDs(uuids)
            .filter((obj): obj is fabric.Object => !!obj);
        this._canvas.removeObjectsSilently(removedObjects);
        this._canvas.requestRenderAll();

        this._dispatchCanvasObjectEvent(
            'objectsremoved:silent',
            removedObjects,
        );
    }

    public clearSelection(): void {
        const active = this._canvas.activeObject;
        // TODO: add better logic to distinguish color change buttons
        if (active && isTextbox(active) && active.isEditing) {
            active.set({
                selectable: this._currentTool === DrawingTool.SELECT,
            });
        }
        this._canvas.requestRenderAll();
    }

    public setDimensions(width: number, height: number): void {
        if (!width || !height) {
            return;
        }

        const MAGIC_NUMBER = 540;
        this._canvas._fabricCanvas.setDimensions({ width, height });
        this._canvas._fabricCanvas.setZoom(height / MAGIC_NUMBER);
        this._canvas._fabricCanvas.calcOffset();
        this._canvas._fabricCanvas.requestRenderAll();
    }

    public removeObjects(objects: fabric.Object[]): void {
        let removedObjects: fabric.Object[];
        if (objects === this.objects) {
            this._canvas._fabricCanvas._objects = [];
            removedObjects = objects;
        } else {
            const uuids = new Set(objects.map(normalizeObjectUUID));
            removedObjects = this.objects.filter((obj) =>
                uuids.has(normalizeObjectUUID(obj)),
            );

            for (const obj of removedObjects) {
                this._canvas.removeObjectSilently(obj);
            }
        }

        for (const obj of removedObjects) {
            this._cachedObjectStates.remove(obj);
            this._history.enqueueAction({
                type: 'removed',
                before: serializeObject(obj),
            });
        }

        this._history.commit();

        this._dispatchCanvasObjectEvent('objectsremoved', removedObjects);

        this._canvas.requestRenderAll();
    }

    public clear(): void {
        this.removeObjects(this.objects);
        this._dispatchCanvasEvent('canvascleared');
    }

    public reset(): void {
        this._history = new StateActionHistory<SerializedObject>({});
        this._canvas.discardActiveObject();
        this._canvas._fabricCanvas._objects = [];
        this._cachedObjectStates.clear();
        this._canvas.requestRenderAll();
    }

    public destroy(): void {
        for (const cleanup of this._cleanupFuncs) {
            cleanup();
        }
    }

    private _fixupBuilderObject(obj: fabric.Object): fabric.Object {
        obj.set({
            cacheProperties: (obj.cacheProperties ?? []).concat([
                'scaleX',
                'scaleY',
            ]),
            fill: obj.fill ?? '',
        });
        obj.setCoords();

        if (isTextbox(obj)) {
            this._canvas._fabricCanvas.setActiveObject(obj);
        }

        return obj;
    }

    private _updateBuilderObject(): void {
        if (!this._objectBuilder) {
            return;
        }

        this._canvas.replaceObjectsSilently(
            this._fixupBuilderObject(this._objectBuilder.object),
            true,
        );
    }

    private _finalizeBuilderObject(allowZeroDimension: boolean): boolean {
        if (!this._objectBuilder) {
            return false;
        }

        const builder = this._objectBuilder;
        this._objectBuilder = undefined;

        this._canvas.discardActiveObject();

        const obj = builder.object;
        this._canvas.removeObjectSilently(obj);

        const finalObj = builder.finalize();
        if (
            !this._dispatchCanvasEvent('drawend', {
                object: finalObj as fabric.Object | undefined,
                cancelable: true,
            })
        ) {
            return false;
        }

        if (!finalObj) {
            return false;
        }

        const fixedObj = this._fixupBuilderObject(finalObj);
        if (
            !allowZeroDimension &&
            (fixedObj.width === 0 || fixedObj.height === 0)
        ) {
            return false;
        }

        if (isTextbox(finalObj)) {
            this._canvas.addObjectsSilently(fixedObj);
            finalObj.enterEditing();
        } else {
            this._canvas.addObjects(fixedObj);
        }

        return true;
    }

    private _dispatchCanvasEvent(
        type: string,
        init: Omit<DrawingCanvasEventInit, 'canvas'> = {},
    ) {
        return this.dispatchEvent(
            new DrawingCanvasEvent(type, {
                ...init,
                canvas: this,
            }),
        );
    }

    private async _dispatchCanvasObjectEvent(
        type: string,
        objects?: fabric.Object | fabric.Object[],
        moving?: boolean,
    ): Promise<boolean> {
        return this._dispatchCanvasEvent(type, {
            objects: objects && (await flattenObjects(objects, moving)),
            moving: objects && (moving || false),
        });
    }
}

export { DrawingTool } from './tools';
export { SerializedObject, SerializedCanvas };
